import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { amrTypeCounts } from "../data/amrIntegration";
import MotionAfterHydration from "./MotionAfterHydration";
import { isPrerendering } from "../utils/isPrerendering";
import { SCOREBOARD_TIMING } from "./home/scoreboardTiming";

const FALLBACK_ICON = "/icons/robot.svg";
const ROLL_EASE = [0.12, 0.82, 0.2, 1];
const STRIP_ONES = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 0, 1, 2, 3, 4, 5, 6, 7, 8, 9];
const STRIP_TENS = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9];

function useClientReady() {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    if (isPrerendering()) return;
    setReady(true);
  }, []);
  return ready;
}

function OdometerDigit({ digit, extraSpin, duration, delay, play, instant, compact }) {
  const ready = useClientReady();
  const strip = extraSpin ? STRIP_ONES : STRIP_TENS;
  const target = extraSpin ? 10 + digit : digit;
  const showFinal = instant;
  const index = showFinal || play ? target : 0;
  const pct = 100 / strip.length;
  const y = `${-index * pct}%`;
  const windowBox = compact
    ? "relative h-11 w-7 overflow-hidden sm:h-12 sm:w-8"
    : "relative h-14 w-9 overflow-hidden sm:h-16 sm:w-10";
  const cell = compact
    ? "flex h-11 w-full shrink-0 items-center justify-center text-2xl font-bold tabular-nums leading-none text-gray-900 sm:h-12 sm:text-3xl"
    : "flex h-14 w-full shrink-0 items-center justify-center text-3xl font-bold tabular-nums leading-none text-gray-900 sm:h-16 sm:text-4xl";

  const stripNodes = strip.map((value, i) => (
    <div key={`${value}-${i}`} className={cell}>
      {value}
    </div>
  ));

  return (
    <div className="border border-gray-900 bg-white">
      <div className={windowBox}>
        <div
          className="pointer-events-none absolute inset-x-0.5 top-1/2 z-10 h-px -translate-y-1/2 bg-gray-400/70"
          aria-hidden
        />
        {!ready || instant ? (
          <div style={{ transform: `translateY(${showFinal ? y : "0%"})` }}>{stripNodes}</div>
        ) : (
          <motion.div
            initial={{ y: "0%" }}
            animate={{ y }}
            transition={{
              duration: play ? duration : 0,
              delay: play ? delay : 0,
              ease: ROLL_EASE,
            }}
          >
            {stripNodes}
          </motion.div>
        )}
      </div>
    </div>
  );
}

function OdometerPair({ value, play, instant, delay, compact }) {
  const tens = Math.floor(value / 10) % 10;
  const ones = value % 10;
  return (
    <div className="flex items-stretch gap-px" aria-hidden="true">
      <OdometerDigit
        digit={tens}
        extraSpin={false}
        duration={SCOREBOARD_TIMING.tensDur}
        delay={delay}
        play={play}
        instant={instant}
        compact={compact}
      />
      <OdometerDigit
        digit={ones}
        extraSpin
        duration={SCOREBOARD_TIMING.onesDur}
        delay={delay + 0.05}
        play={play}
        instant={instant}
        compact={compact}
      />
    </div>
  );
}

function CounterItem({ label, count, image, width, height, compact, index, play, instant }) {
  const [imgError, setImgError] = useState(false);
  const imgSrc = image && !imgError ? image : FALLBACK_ICON;
  const vehicleDelay = index * SCOREBOARD_TIMING.vehicleStagger;
  const digitDelay = SCOREBOARD_TIMING.digitsAt + index * SCOREBOARD_TIMING.vehicleStagger;
  const visible = instant || play;

  const iconBox = compact
    ? "flex h-32 w-full items-end justify-center"
    : "flex h-36 w-full items-end justify-center";

  return (
    <div className={`flex min-w-0 flex-col items-center px-1.5 py-2 ${index === 4 ? "max-sm:col-span-2" : ""}`}>
      <MotionAfterHydration
        className="flex w-full flex-col items-center"
        initial={instant ? false : { opacity: 0, y: 16, scale: 0.94 }}
        animate={{
          opacity: visible ? 1 : 0,
          y: visible ? 0 : 16,
          scale: visible ? 1 : 0.94,
        }}
        transition={{
          duration: play ? SCOREBOARD_TIMING.vehicleRise : 0,
          delay: play ? vehicleDelay : 0,
          ease: [0.16, 1, 0.3, 1],
        }}
      >
        <div className={`${iconBox} overflow-visible`}>
          <img
            src={imgSrc}
            alt={label}
            width={imgError || !image ? 512 : width}
            height={imgError || !image ? 512 : height}
            className={
              label === "Pallet"
                ? "origin-bottom h-[130%] w-auto max-w-none translate-x-2 object-contain object-bottom"
                : "h-full w-auto max-w-full object-contain object-bottom"
            }
            onError={() => {
              if (isPrerendering()) return;
              setImgError(true);
            }}
          />
        </div>
      </MotionAfterHydration>

      <div className="mt-3 flex flex-col items-center gap-1.5">
        <OdometerPair
          value={count}
          play={play}
          instant={instant}
          delay={digitDelay}
          compact={compact}
        />
        <span className="text-[10px] font-semibold uppercase tracking-[0.22em] text-gray-500">
          {label}
        </span>
      </div>
    </div>
  );
}

export default function AMRTypeTicker({ compact = false, play = false, instant = true }) {
  const items = amrTypeCounts.filter((item) => item.count != null && item.count >= 0);

  if (items.length === 0) return null;

  return (
    <div
      className={compact ? "mt-6 sm:mt-8" : "mt-8 sm:mt-10"}
      aria-label="AMR type integration counts"
    >
      <ul className="sr-only">
        {items.map((item) => (
          <li key={item.type}>{`${item.label}: ${String(item.count).padStart(2, "0")}`}</li>
        ))}
      </ul>
      <div className="grid grid-cols-2 sm:grid-cols-5">
        {items.map((item, idx) => (
          <CounterItem
            key={item.type}
            label={item.label}
            count={item.count}
            image={item.image}
            width={item.width}
            height={item.height}
            compact={compact}
            index={idx}
            play={play}
            instant={instant}
          />
        ))}
      </div>
    </div>
  );
}
